import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiFamilyInvitePreview, apiFamilyAccept } from '../utils/api';

function getTokenFromUrl() {
  return new URLSearchParams(window.location.search).get('token') || '';
}

export default function FamilyAcceptPage({ onDone }) {
  const { user, isAuthenticated, login, register } = useAuth();
  const [token] = useState(getTokenFromUrl);

  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError,   setPreviewError]   = useState('');
  const [invite,         setInvite]         = useState(null);

  const [mode,     setMode]     = useState('login'); // login | register
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const [accepting, setAccepting] = useState(false);
  const [accepted,  setAccepted]  = useState(false);

  useEffect(() => {
    if (!token) { setPreviewError('Invalid invite link — token missing.'); setPreviewLoading(false); return; }
    apiFamilyInvitePreview(token)
      .then(data => { setInvite(data); setPreviewLoading(false); })
      .catch(err => { setPreviewError(err.message); setPreviewLoading(false); });
  }, [token]);

  async function handleAccept() {
    setAccepting(true); setError('');
    try {
      await apiFamilyAccept(token);
      setAccepted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  }

  async function handleLogin(e) {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      await login(username, password);
      await handleAccept();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault(); setError('');
    if (password !== confirm) return setError('Passwords do not match');
    if (password.length < 8)  return setError('Password must be at least 8 characters');
    setLoading(true);
    try {
      await register(username, password, invite?.email);
      await handleAccept();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const shell = (children) => (
    <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
      <div className="auth-card">{children}</div>
    </div>
  );

  if (previewLoading) {
    return shell(
      <div className="auth-logo">
        <span className="logo-icon">👪</span>
        <h1>Family invite</h1>
        <p className="auth-tagline">Checking invite…</p>
      </div>
    );
  }

  if (previewError) {
    return shell(<>
      <div className="auth-logo">
        <span className="logo-icon">⚠️</span>
        <h1>Invalid invite</h1>
      </div>
      <div className="auth-error">⚠ {previewError}</div>
      <button className="btn-primary" onClick={onDone}>Back to home</button>
    </>);
  }

  if (accepted) {
    return shell(<>
      <div className="auth-logo">
        <span className="logo-icon">✅</span>
        <h1>Done!</h1>
        <p className="auth-tagline">You're now part of @{invite.ownerUsername}'s family</p>
      </div>
      <button className="btn-primary" onClick={onDone}>Go to your Vault →</button>
    </>);
  }

  // ── Already logged in — confirm join ─────────────────────
  if (isAuthenticated) {
    return shell(<>
      <div className="auth-logo">
        <span className="logo-icon">👪</span>
        <h1>Family invite</h1>
        <p className="auth-tagline"><strong>@{invite.ownerUsername}</strong> invited you to join their family</p>
      </div>
      <div className="auth-notice">
        <span>🔒</span>
        <p>You're signed in as <strong>@{user?.username}</strong>. Your files stay private and encrypted — you'll only share the storage space.</p>
      </div>
      {error && <div className="auth-error">⚠ {error}</div>}
      <button className="btn-primary" onClick={handleAccept} disabled={accepting}>
        {accepting ? 'Please wait…' : `✓ Join @${invite.ownerUsername}'s family`}
      </button>
    </>);
  }

  // ── Not logged in — embedded login / register ────────────
  return shell(<>
    <div className="auth-logo">
      <span className="logo-icon">👪</span>
      <h1>Family invite</h1>
      <p className="auth-tagline"><strong>@{invite.ownerUsername}</strong> invited you to LifetimeCloud</p>
    </div>

    <div className="auth-tabs">
      {['login','register'].map(m => (
        <button key={m} className={mode===m?'active':''} onClick={()=>{setMode(m);setError('');}}>
          {m==='login'?'I already have an account':'Create account'}
        </button>
      ))}
    </div>

    <form onSubmit={mode==='login'?handleLogin:handleRegister} className="auth-form">
      <div className="field">
        <label>Username</label>
        <input type="text" value={username} onChange={e=>setUsername(e.target.value)}
          placeholder="your_username" required autoComplete="username"/>
      </div>

      <div className="field">
        <label>Password</label>
        <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
          placeholder="••••••••" required autoComplete={mode==='login'?'current-password':'new-password'}/>
      </div>

      {mode === 'register' && (
        <div className="field">
          <label>Confirm password</label>
          <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
            placeholder="••••••••" required autoComplete="new-password"/>
        </div>
      )}

      {error && <div className="auth-error">⚠ {error}</div>}

      <button type="submit" className="btn-primary" disabled={loading || accepting}>
        {loading || accepting ? 'Processing…' : mode==='login' ? 'Sign in and join' : 'Create account and join'}
      </button>
    </form>
  </>);
}
